import { useEffect } from "react";

/**
 * Handle Android's hardware back button while something is open over the page.
 *
 * A Story, a composer or a sheet is drawn in a portal rather than pushed as a
 * route, so Ionic's own back handler knows nothing about it: pressing back
 * popped the page UNDERNEATH and left the Community entirely — from the
 * member's point of view the app jumped to the home page while a Story was
 * still on screen.
 *
 * `ionBackButton` is the right hook rather than Capacitor's raw `backButton`
 * listener: Ionic collects every registered handler, sorts them by priority and
 * runs the highest first, so a priority above Ionic's own router (100) lets an
 * overlay consume the press. Not calling `next()` is what stops the navigation.
 *
 * @param {boolean} active   Register only while the overlay is actually open.
 * @param {() => void} onBack What to do instead of navigating — usually close.
 */
export function useHardwareBack(active, onBack) {
  useEffect(() => {
    if (!active) return undefined;

    const handler = (event) => {
      // 150 > Ionic's router (100), so this runs first and the route stays put.
      event.detail.register(150, () => {
        onBack();
      });
    };

    document.addEventListener("ionBackButton", handler);
    return () => document.removeEventListener("ionBackButton", handler);
  }, [active, onBack]);
}
